import { Classes } from "@blueprintjs/core";
import React, { useCallback } from "react";
import styled, { css } from "styled-components";

import { HexButton } from "../../../../hex-components/HexButton.js";
import {
  HexKeyCombo,
  HexKeyComboProps,
} from "../../../../hex-components/HexKeyCombo.js";
import { Txt } from "../../../../hex-components/Txt.js";
import { maybePluralize } from "../../../../util/string.js";
import {
  ColumnRowTop,
  ColumnRowWrapper,
  ColumnTypeBadge,
  ColumnTypeBadgeWrapper,
  TextContainer,
} from "../../../data/schema-browser-v2/SharedStyles.js";
import { Heading } from "../../../Heading.js";
import { WarehouseTableIcon } from "../../../icons/CustomIcons.js";

export const DataPillTooltipContentWrapper = styled(
  ({ onClick, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        onClick?.(event);
      },
      [onClick],
    );
    return <div {...props} onClick={handleClick} />;
  },
)`
  padding: 7px 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 204px;
  max-width: 400px;
  width: max-content;

  color: ${({ theme }) => theme.fontColor.MUTED};
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ColumnItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  color: ${({ theme }) => theme.fontColor.DEFAULT};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  font-family: ${({ theme }) => theme.fontFamily.MONO};
`;

const ParamName = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.MONO};
  font-size: ${({ theme }) => theme.fontSize.SMALL};
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.fontColor.DEFAULT};
`;

const ParamType = styled.div`
  margin-left: 8px;

  color: ${({ theme }) => theme.fontColor.MUTED};
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};

  white-space: nowrap;
  text-transform: lowercase;
`;

const DataSourceTableIcon = styled(WarehouseTableIcon)`
  &&&& {
    margin: 0;
  }
`;

const NUM_COLUMNS_TO_SHOW = 5;

export interface DataPillTooltipContentProps {
  icon?: "data-source-table";
  tableName: string;
  description: string | null;
  columns: ReadonlyArray<{ name: string; type: string }>;
  tableType?: string;

  // optional action at the bottom of the tooltip content
  actionName?: React.ReactNode;
  actionKeyCombo?: HexKeyComboProps["combo"];
  onAction?: (event: React.MouseEvent) => void;
  /** A callback fired when the tooltip content popover is dismissed. */
  onPopoverDismiss?: () => void;
}

export const DataPillTooltipContent: React.ComponentType<DataPillTooltipContentProps> =
  React.memo(function DataPillTooltipContent({
    actionKeyCombo,
    actionName,
    columns,
    description,
    icon,
    onAction,
    onPopoverDismiss,
    tableName,
    tableType,
  }) {
    const numColumns = columns.length;
    const numAdditionalColumns = numColumns - NUM_COLUMNS_TO_SHOW;

    const handleActionClick = useCallback(
      (event: React.MouseEvent) => {
        event.stopPropagation();
        onAction?.(event);
        onPopoverDismiss?.();
      },
      [onAction, onPopoverDismiss],
    );

    return (
      <>
        <ParamName>
          {icon === "data-source-table" ? <DataSourceTableIcon /> : undefined}
          <Txt ellipsize={true} title={tableName}>
            {tableName}
          </Txt>
          {tableType && <ParamType>{tableType}</ParamType>}
        </ParamName>
        {description && (
          <Txt ellipsize={true} title={description}>
            {description}
          </Txt>
        )}
        {numColumns > 0 && (
          <ColumnWrapper>
            <Heading renderAs="h5">Columns</Heading>
            <ColumnItems>
              {columns.slice(0, NUM_COLUMNS_TO_SHOW).map((column) => {
                return (
                  <div key={column.name}>
                    <ColumnRowWrapper>
                      <ColumnRowTop>
                        <TextContainer>
                          <Txt ellipsize={true} title={column.name}>
                            {column.name}
                          </Txt>
                        </TextContainer>
                        <ColumnTypeBadgeWrapper>
                          <ColumnTypeBadge
                            className={Classes.TEXT_OVERFLOW_ELLIPSIS}
                            title={column.type}
                          >
                            {column.type}
                          </ColumnTypeBadge>
                        </ColumnTypeBadgeWrapper>
                      </ColumnRowTop>
                    </ColumnRowWrapper>
                  </div>
                );
              })}
            </ColumnItems>
            {numColumns > NUM_COLUMNS_TO_SHOW && (
              <Txt fontSize="extra_small">
                {numAdditionalColumns} more{" "}
                {maybePluralize(numAdditionalColumns, "column", "columns")}...
              </Txt>
            )}
          </ColumnWrapper>
        )}
        {actionName && onAction && (
          <HexButton intent="primary" onClick={handleActionClick}>
            {actionKeyCombo ? (
              <ActionNameKeyWrapper>
                <span>{actionName}</span>
                <StyledHexKeyCombo combo={actionKeyCombo} />
              </ActionNameKeyWrapper>
            ) : (
              actionName
            )}
          </HexButton>
        )}
      </>
    );
  });

const ActionNameKeyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledHexKeyCombo = styled((props: HexKeyComboProps) => (
  <HexKeyCombo $color="inherit" {...props} />
))`
  ${({ theme }) => {
    return css`
      box-shadow: inset 0 0 0 1px ${theme.button.default.primary.borderColor};
    `;
  }};
`;
